/**
 * Current-tenant identity.
 *
 * Lives in `platform/` rather than under the admin feature because the app
 * shell needs it: `components/layout/TenantBadge` renders on every
 * authenticated route, so sourcing it from `features/admin` put the admin
 * feature in the entry chunk. Tenant *administration* (mutations, search)
 * stays in `features/admin/api/tenants`.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useApi } from '@/hooks/useAppApi'
import type { TenantMeResponse } from '@shared/types/tenant'

/**
 * Query key for the current tenant. Exported so admin mutations can invalidate
 * it without duplicating the literal.
 */
export const MY_TENANT_QUERY_KEY = ['tenants', 'me'] as const

export function useMyTenant(): UseQueryResult<TenantMeResponse> {
  const api = useApi()
  return useQuery<TenantMeResponse>({
    queryKey: MY_TENANT_QUERY_KEY,
    queryFn: () => api.get('/tenants/me') as Promise<TenantMeResponse>,
  })
}
